// ===========================================================================
// CDCI — Base de datos fake en memoria.
//
// Simula lo que servirán el backend MCM.CORE y AdvancedMD mientras se
// implementan las entidades del roadmap (Appointment, Note, Charge).
// La forma de cada objeto replica los contratos previstos en
// docs/modulos/BillingV2/02_CDCI_Descripcion_Modulo.md §6 para que el swap a
// endpoints reales sea solo cambiar la capa de servicio.
//
// El estado mutable (notas, cargos, estados de cita) vive en memoria y
// persiste solo durante la sesión del navegador.
// ===========================================================================

// --- Maestro de locations (réplica de mdm.TAB_AMD_LOCATIONS) ---------------
export const FAKE_LOCATIONS = [
  {
    locationId: 1,
    code: "SP-FM",
    name: "Sante Plus — Fort Myers",
    address: "4071 Evans Ave, Fort Myers, FL 33901",
    phone: "(239) 555-0134",
    pos: "11",
    active: true,
    // Payloads pre-construidos que se inyectan en addehrnote (location/1/2)
    payloads: {
      location: "Sante Plus Fort Myers",
      location1: "4071 Evans Ave, Fort Myers, FL 33901 · (239) 555-0134",
      location2: "POS 11 — Office",
    },
  },
  {
    locationId: 2,
    code: "SP-NP",
    name: "Sante Plus — Naples",
    address: "2335 Tamiami Trail N, Naples, FL 34103",
    phone: "(239) 555-0177",
    pos: "11",
    active: true,
    payloads: {
      location: "Sante Plus Naples",
      location1: "2335 Tamiami Trail N, Naples, FL 34103 · (239) 555-0177",
      location2: "POS 11 — Office",
    },
  },
];

export const FAKE_PROVIDERS = [
  { providerId: 101, name: "Dr. Elena Vargas, MD", npi: "1538274610", locationId: 1 },
  { providerId: 102, name: "Dr. Marcus Reed, DO", npi: "1841920375", locationId: 2 },
];

// --- Pacientes con su contexto clínico (lo que servirá el agregador 4.2) ---
export const FAKE_PATIENTS = [
  {
    patientId: 5001,
    cipId: 93750482101,
    name: "RODRIGUEZ, MARIA",
    age: 67, gender: "F", dob: "1958-11-02",
    membership: "Medicare Advantage — Simply",
    pcp: "Dr. Elena Vargas",
    insurance: { primary: "Simply Healthcare (MA HMO)", memberId: "SH88412035", eligibility: "Active" },
    allergies: [
      { allergen: "Penicillin", reaction: "Hives", severity: "Moderate" },
      { allergen: "Sulfa drugs", reaction: "Rash", severity: "Mild" },
    ],
    medications: [
      { name: "Metformin 1000 mg", sig: "1 tab PO BID", lastFill: "2026-05-21" },
      { name: "Lisinopril 20 mg", sig: "1 tab PO daily", lastFill: "2026-05-21" },
      { name: "Atorvastatin 40 mg", sig: "1 tab PO QHS", lastFill: "2026-04-30" },
    ],
    labs: [
      { test: "HbA1c", value: "8.2 %", flag: "H", date: "2026-05-12" },
      { test: "eGFR", value: "54 mL/min", flag: "L", date: "2026-05-12" },
      { test: "LDL", value: "96 mg/dL", flag: "", date: "2026-05-12" },
    ],
    previousNotes: [
      { date: "2026-03-04", template: "Follow Up Exam 2.0", provider: "Dr. Elena Vargas", signed: true },
      { date: "2025-12-10", template: "Annual Wellness 2.0", provider: "Dr. Elena Vargas", signed: true },
    ],
    activeProblems: ["E11.42 — T2DM w/ diabetic polyneuropathy", "I10 — Essential hypertension", "N18.3 — CKD stage 3"],
  },
  {
    patientId: 5002,
    cipId: 66250096412,
    name: "JOHNSON, WALTER",
    age: 72, gender: "M", dob: "1953-07-19",
    membership: "Medicare Advantage — Humana",
    pcp: "Dr. Elena Vargas",
    insurance: { primary: "Humana Gold Plus (MA HMO)", memberId: "HU55230981", eligibility: "Active" },
    allergies: [],
    medications: [
      { name: "Furosemide 40 mg", sig: "1 tab PO daily", lastFill: "2026-06-01" },
      { name: "Carvedilol 12.5 mg", sig: "1 tab PO BID", lastFill: "2026-06-01" },
      { name: "Eliquis 5 mg", sig: "1 tab PO BID", lastFill: "2026-05-28" },
      { name: "Potassium Cl 20 mEq", sig: "1 tab PO daily", lastFill: "2026-06-01" },
    ],
    labs: [
      { test: "BNP", value: "412 pg/mL", flag: "H", date: "2026-06-05" },
      { test: "Potassium", value: "3.4 mmol/L", flag: "L", date: "2026-06-05" },
      { test: "Creatinine", value: "1.4 mg/dL", flag: "H", date: "2026-06-05" },
    ],
    previousNotes: [
      { date: "2026-06-06", template: "Hospital Discharge Follow Up", provider: "Dr. Elena Vargas", signed: true },
      { date: "2026-04-15", template: "Follow Up Exam 2.0", provider: "Dr. Elena Vargas", signed: true },
    ],
    activeProblems: ["I50.32 — Chronic diastolic CHF", "I48.91 — Atrial fibrillation", "I10 — Essential hypertension"],
  },
  {
    patientId: 5003,
    cipId: 30000325744,
    name: "PAZERA, JAMIE",
    age: 58, gender: "F", dob: "1967-09-23",
    membership: "Medicare Advantage — Simply",
    pcp: "Dr. Elena Vargas",
    insurance: { primary: "Simply Healthcare (MA HMO)", memberId: "SH77120443", eligibility: "Active" },
    allergies: [{ allergen: "Codeine", reaction: "Nausea", severity: "Mild" }],
    medications: [
      { name: "Sertraline 100 mg", sig: "1 tab PO daily", lastFill: "2026-05-15" },
      { name: "Gabapentin 300 mg", sig: "1 cap PO TID", lastFill: "2026-05-15" },
      { name: "Omeprazole 20 mg", sig: "1 cap PO daily", lastFill: "2026-05-02" },
    ],
    labs: [
      { test: "TSH", value: "2.1 mIU/L", flag: "", date: "2026-02-20" },
      { test: "CMP", value: "WNL", flag: "", date: "2026-02-20" },
    ],
    previousNotes: [
      { date: "2026-02-21", template: "TELEHEALTH_2.0", provider: "Dr. Elena Vargas", signed: true },
    ],
    activeProblems: ["F33.1 — MDD recurrent moderate", "M79.7 — Fibromyalgia", "K21.9 — GERD"],
  },
  {
    patientId: 5004,
    cipId: 42500094233,
    name: "SHERLOCK, KYLE",
    age: 61, gender: "M", dob: "1964-12-30",
    membership: "Medicare Advantage — Devoted",
    pcp: "Dr. Elena Vargas",
    insurance: { primary: "Devoted Health (MA HMO)", memberId: "DV33108852", eligibility: "Active" },
    allergies: [{ allergen: "Latex", reaction: "Contact dermatitis", severity: "Moderate" }],
    medications: [
      { name: "Insulin glargine 30 u", sig: "30 units SQ QHS", lastFill: "2026-06-02" },
      { name: "Aspirin 81 mg", sig: "1 tab PO daily", lastFill: "2026-06-02" },
      { name: "Metoprolol succinate 50 mg", sig: "1 tab PO daily", lastFill: "2026-06-02" },
    ],
    labs: [
      { test: "HbA1c", value: "9.1 %", flag: "H", date: "2026-06-08" },
      { test: "LDL", value: "131 mg/dL", flag: "H", date: "2026-06-08" },
    ],
    previousNotes: [
      { date: "2026-06-09", template: "Hospital Discharge Follow Up", provider: "Dr. Elena Vargas", signed: true },
    ],
    activeProblems: ["E11.65 — T2DM w/ hyperglycemia", "I25.10 — CAD native artery", "Z95.5 — Coronary stent"],
  },
  {
    patientId: 5005,
    cipId: 18750261099,
    name: "MCQUINN, MICHELE",
    age: 58, gender: "F", dob: "1968-03-15",
    membership: "Medicare Advantage — Simply",
    pcp: "Dr. Elena Vargas",
    insurance: { primary: "Simply Healthcare (MA HMO)", memberId: "SH90233176", eligibility: "Active" },
    sensitive: true,
    allergies: [],
    medications: [
      { name: "Morphine SR 30 mg", sig: "1 tab PO BID", lastFill: "2026-06-03" },
      { name: "Ondansetron 4 mg", sig: "1 tab PO Q8H PRN", lastFill: "2026-06-03" },
    ],
    labs: [{ test: "CBC", value: "See report", flag: "H", date: "2026-05-30" }],
    previousNotes: [
      { date: "2026-05-30", template: "Follow Up Exam 2.0", provider: "Dr. Elena Vargas", signed: true },
    ],
    activeProblems: ["C50.911 — Breast cancer, right", "Z51.11 — Chemotherapy encounter", "R52 — Chronic pain"],
  },
  {
    patientId: 5006,
    cipId: 55120087310,
    name: "NGUYEN, THANH",
    age: 69, gender: "M", dob: "1956-08-08",
    membership: "Medicare Advantage — Humana",
    pcp: "Dr. Elena Vargas",
    insurance: { primary: "Humana Gold Plus (MA HMO)", memberId: "HU41099287", eligibility: "Active" },
    allergies: [{ allergen: "Shellfish", reaction: "Anaphylaxis", severity: "Severe" }],
    medications: [
      { name: "Tiotropium 18 mcg", sig: "1 inh daily", lastFill: "2026-05-25" },
      { name: "Albuterol HFA", sig: "2 puffs Q6H PRN", lastFill: "2026-05-25" },
      { name: "Amlodipine 10 mg", sig: "1 tab PO daily", lastFill: "2026-05-25" },
    ],
    labs: [{ test: "Spirometry FEV1", value: "58 %", flag: "L", date: "2026-04-18" }],
    previousNotes: [
      { date: "2026-04-18", template: "Annual Wellness 2.0", provider: "Dr. Elena Vargas", signed: true },
    ],
    activeProblems: ["J44.1 — COPD w/ acute exacerbation", "I10 — Essential hypertension", "Z87.891 — Hx of nicotine dependence"],
  },
];

// --- Citas del día (futura entidad Appointment / REST Scheduler) ------------
// Las horas son relativas al día seleccionado; el servicio las materializa.
export const FAKE_APPOINTMENTS = [
  { appointmentId: 9001, time: "08:00", durationMin: 30, patientId: 5001, providerId: 101, locationId: 1, typeKey: "AWV",        statusKey: "COMPLETED" },
  { appointmentId: 9002, time: "08:30", durationMin: 20, patientId: 5002, providerId: 101, locationId: 1, typeKey: "HOSP_FU",    statusKey: "COMPLETED" },
  { appointmentId: 9003, time: "09:00", durationMin: 20, patientId: 5003, providerId: 101, locationId: 1, typeKey: "TELEHEALTH", statusKey: "IN_ROOM" },
  { appointmentId: 9004, time: "09:30", durationMin: 20, patientId: 5004, providerId: 101, locationId: 1, typeKey: "HOSP_FU",    statusKey: "CHECKED_IN" },
  { appointmentId: 9005, time: "10:00", durationMin: 30, patientId: 5005, providerId: 101, locationId: 1, typeKey: "FOLLOW_UP",  statusKey: "CHECKED_IN" },
  { appointmentId: 9006, time: "10:30", durationMin: 20, patientId: 5006, providerId: 101, locationId: 1, typeKey: "FOLLOW_UP",  statusKey: "CONFIRMED" },
  { appointmentId: 9007, time: "11:00", durationMin: 30, patientId: 5001, providerId: 102, locationId: 2, typeKey: "FOLLOW_UP",  statusKey: "SCHEDULED" },
  { appointmentId: 9008, time: "11:30", durationMin: 20, patientId: 5003, providerId: 101, locationId: 1, typeKey: "SICK",       statusKey: "SCHEDULED" },
  { appointmentId: 9009, time: "13:00", durationMin: 30, patientId: 5006, providerId: 101, locationId: 1, typeKey: "AWV",        statusKey: "SCHEDULED" },
  { appointmentId: 9010, time: "13:30", durationMin: 20, patientId: 5002, providerId: 101, locationId: 1, typeKey: "FOLLOW_UP",  statusKey: "NO_SHOW" },
];

// --- Plantillas de nota (getehrtemplates / getehrnotestemplate) -------------
// Estructura pagelist/page/fieldlist/field de AdvancedMD, simplificada.
// fieldname/ordinal replican lo que exigirá addehrnote.
const VITALS_PAGE = {
  pageName: "Vitals",
  fields: [
    { fieldname: "bp_systolic",  ordinal: 1, label: "BP Systolic",  type: "number", unit: "mmHg" },
    { fieldname: "bp_diastolic", ordinal: 2, label: "BP Diastolic", type: "number", unit: "mmHg" },
    { fieldname: "heart_rate",   ordinal: 3, label: "Heart Rate",   type: "number", unit: "bpm" },
    { fieldname: "temp",         ordinal: 4, label: "Temperature",  type: "number", unit: "°F" },
    { fieldname: "weight",       ordinal: 5, label: "Weight",       type: "number", unit: "lb" },
    { fieldname: "bmi",          ordinal: 6, label: "BMI",          type: "number", unit: "" },
  ],
};

const VISIT_INFO_PAGE = (templateName) => ({
  pageName: "Visit Info",
  fields: [
    { fieldname: "location",   ordinal: 1, label: "Location",        type: "readonly", prefill: "location" },
    { fieldname: "location1",  ordinal: 2, label: "Location Detail", type: "readonly", prefill: "location1" },
    { fieldname: "location2",  ordinal: 3, label: "Place of Service", type: "readonly", prefill: "location2" },
    { fieldname: "visit_date", ordinal: 4, label: "Visit Date",      type: "readonly", prefill: "visitDate" },
    { fieldname: "patient",    ordinal: 5, label: "Patient",         type: "readonly", prefill: "patientName" },
    { fieldname: "template",   ordinal: 6, label: "Template",        type: "readonly", prefill: templateName },
  ],
});

export const FAKE_NOTE_TEMPLATES = [
  {
    templateName: "Annual Wellness 2.0",
    appliesTo: ["AWV"],
    pages: [
      VISIT_INFO_PAGE("Annual Wellness 2.0"),
      VITALS_PAGE,
      {
        pageName: "Health Risk Assessment",
        fields: [
          { fieldname: "fall_risk",       ordinal: 1, label: "Fall Risk Screening",      type: "select", options: ["Negative", "Positive"] },
          { fieldname: "depression_phq2", ordinal: 2, label: "Depression (PHQ-2)",       type: "select", options: ["0", "1", "2", "3", "4", "5", "6"] },
          { fieldname: "cognitive",       ordinal: 3, label: "Cognitive Assessment",     type: "select", options: ["Normal", "Abnormal — follow up"] },
          { fieldname: "advance_care",    ordinal: 4, label: "Advance Care Plan reviewed", type: "select", options: ["Yes", "No", "Declined"] },
        ],
      },
      {
        pageName: "Assessment & Plan",
        fields: [
          { fieldname: "assessment", ordinal: 1, label: "Assessment", type: "textarea", required: true },
          { fieldname: "plan",       ordinal: 2, label: "Plan",       type: "textarea", required: true },
          { fieldname: "screenings", ordinal: 3, label: "Preventive screenings ordered", type: "textarea" },
        ],
      },
    ],
  },
  {
    templateName: "Follow Up Exam 2.0",
    appliesTo: ["FOLLOW_UP", "SICK"],
    pages: [
      VISIT_INFO_PAGE("Follow Up Exam 2.0"),
      VITALS_PAGE,
      {
        pageName: "Subjective / ROS",
        fields: [
          { fieldname: "chief_complaint", ordinal: 1, label: "Chief Complaint", type: "text", required: true },
          { fieldname: "hpi",             ordinal: 2, label: "HPI",             type: "textarea", required: true },
          { fieldname: "ros",             ordinal: 3, label: "Review of Systems", type: "textarea" },
        ],
      },
      {
        pageName: "Exam",
        fields: [{ fieldname: "physical_exam", ordinal: 1, label: "Physical Exam", type: "textarea", required: true }],
      },
      {
        pageName: "Assessment & Plan",
        fields: [
          { fieldname: "assessment", ordinal: 1, label: "Assessment", type: "textarea", required: true },
          { fieldname: "plan",       ordinal: 2, label: "Plan",       type: "textarea", required: true },
        ],
      },
    ],
  },
  {
    templateName: "TELEHEALTH_2.0",
    appliesTo: ["TELEHEALTH"],
    pages: [
      VISIT_INFO_PAGE("TELEHEALTH_2.0"),
      {
        pageName: "Telehealth Consent",
        fields: [
          { fieldname: "consent",  ordinal: 1, label: "Verbal consent obtained", type: "select", options: ["Yes", "No"], required: true },
          { fieldname: "modality", ordinal: 2, label: "Modality", type: "select", options: ["Video", "Audio only"], required: true },
        ],
      },
      {
        pageName: "Subjective",
        fields: [
          { fieldname: "chief_complaint", ordinal: 1, label: "Chief Complaint", type: "text", required: true },
          { fieldname: "hpi",             ordinal: 2, label: "HPI",             type: "textarea", required: true },
        ],
      },
      {
        pageName: "Assessment & Plan",
        fields: [
          { fieldname: "assessment", ordinal: 1, label: "Assessment", type: "textarea", required: true },
          { fieldname: "plan",       ordinal: 2, label: "Plan",       type: "textarea", required: true },
        ],
      },
    ],
  },
  {
    templateName: "Hospital Discharge Follow Up",
    appliesTo: ["HOSP_FU"],
    pages: [
      VISIT_INFO_PAGE("Hospital Discharge Follow Up"),
      VITALS_PAGE,
      {
        pageName: "Discharge Review",
        fields: [
          { fieldname: "discharge_date",     ordinal: 1, label: "Discharge Date",            type: "text", required: true },
          { fieldname: "facility",           ordinal: 2, label: "Facility",                  type: "text", required: true },
          { fieldname: "discharge_dx",       ordinal: 3, label: "Discharge Diagnoses",       type: "textarea", required: true },
          { fieldname: "med_reconciliation", ordinal: 4, label: "Medication Reconciliation", type: "select", options: ["Completed", "Pending"], required: true },
        ],
      },
      {
        pageName: "Assessment & Plan",
        fields: [
          { fieldname: "assessment", ordinal: 1, label: "Assessment", type: "textarea", required: true },
          { fieldname: "plan",       ordinal: 2, label: "Plan",       type: "textarea", required: true },
        ],
      },
    ],
  },
];

// --- Catálogo CPT (lookupproccode) ------------------------------------------
// IMPORTANTE: savecharges necesita el ID INTERNO de AdvancedMD (procId),
// no el código CPT — por eso el catálogo lleva ambos.
export const FAKE_PROC_CODES = [
  { procId: 70011, cpt: "99202", description: "Office visit, new patient, straightforward", fee: 75 },
  { procId: 70012, cpt: "99203", description: "Office visit, new patient, low complexity", fee: 115 },
  { procId: 70013, cpt: "99204", description: "Office visit, new patient, moderate complexity", fee: 170 },
  { procId: 70021, cpt: "99212", description: "Office visit, established, straightforward", fee: 58 },
  { procId: 70022, cpt: "99213", description: "Office visit, established, low complexity", fee: 92 },
  { procId: 70023, cpt: "99214", description: "Office visit, established, moderate complexity", fee: 132 },
  { procId: 70024, cpt: "99215", description: "Office visit, established, high complexity", fee: 185 },
  { procId: 70031, cpt: "G0438", description: "Annual Wellness Visit, initial", fee: 175 },
  { procId: 70032, cpt: "G0439", description: "Annual Wellness Visit, subsequent", fee: 120 },
  { procId: 70041, cpt: "99495", description: "TCM, moderate complexity (14-day)", fee: 168 },
  { procId: 70042, cpt: "99496", description: "TCM, high complexity (7-day)", fee: 228 },
  { procId: 70051, cpt: "G2012", description: "Brief virtual check-in", fee: 15 },
  { procId: 70052, cpt: "99443", description: "Telephone E/M, 21-30 min", fee: 110 },
  { procId: 70061, cpt: "96127", description: "Brief behavioral assessment (PHQ)", fee: 6 },
  { procId: 70062, cpt: "1111F", description: "Discharge medications reconciled (CPT II)", fee: 0 },
];

// --- Catálogo ICD-10 (lookupdiagcode) ---------------------------------------
export const FAKE_DIAG_CODES = [
  { code: "E11.9",   description: "Type 2 diabetes mellitus without complications", hcc: "HCC 19" },
  { code: "E11.42",  description: "T2DM with diabetic polyneuropathy", hcc: "HCC 18" },
  { code: "E11.65",  description: "T2DM with hyperglycemia", hcc: "HCC 18" },
  { code: "I10",     description: "Essential (primary) hypertension", hcc: null },
  { code: "I50.32",  description: "Chronic diastolic (congestive) heart failure", hcc: "HCC 85" },
  { code: "I48.91",  description: "Unspecified atrial fibrillation", hcc: "HCC 96" },
  { code: "N18.3",   description: "Chronic kidney disease, stage 3", hcc: "HCC 138" },
  { code: "J44.1",   description: "COPD with acute exacerbation", hcc: "HCC 111" },
  { code: "F33.1",   description: "Major depressive disorder, recurrent, moderate", hcc: "HCC 59" },
  { code: "M79.7",   description: "Fibromyalgia", hcc: null },
  { code: "K21.9",   description: "GERD without esophagitis", hcc: null },
  { code: "I25.10",  description: "ASHD of native coronary artery without angina", hcc: "HCC 88" },
  { code: "C50.911", description: "Malignant neoplasm of breast, right female", hcc: "HCC 12" },
  { code: "R52",     description: "Pain, unspecified", hcc: null },
  { code: "Z00.00",  description: "General adult medical exam without abnormal findings", hcc: null },
  { code: "Z51.11",  description: "Encounter for antineoplastic chemotherapy", hcc: null },
];

// --- Estado mutable de la sesión --------------------------------------------
// notas y cargos por appointmentId + overrides de estado de cita.
export const sessionStore = {
  notes: new Map(),      // appointmentId -> { noteId, templateName, fields: {fieldname: value}, status, signedAt }
  charges: new Map(),    // appointmentId -> { status, procedures: [...], diagnoses: [...], savedAt }
  apptStatus: new Map(), // appointmentId -> statusKey override
};

let noteSeq = 33000;
export const nextNoteId = () => ++noteSeq;

// Latencia simulada de red para que la UI ejercite estados de carga reales.
export const simulateLatency = (ms = 350) =>
  new Promise((resolve) => setTimeout(resolve, ms));
